import { createSlice } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import { RootState } from '..';

import { IUser } from '../UserSlice';
import { IQuiz } from '../QuizSlice';
import { IQuestion, IQuestionDetailAnswer } from '../QuestionSlice';
import {
  getSubmissionById,
  createSubmissionAnswer,
  finishSubmission,
  getSubmissionQuestion,
  showSubmissionQuestionAnswer,
  getSubmissionsForQuizHistory,
  loadMoreSubmissionsForQuizHistory,
  updateSubmissionCountdownSettings,
  startSubmissionQuestionTimer,
  endSubmissionQuestionTimer,
  createNewSubmissionBySubTopic,
  createNewSubmissionByQuizId,
  updateSubmissionQuestionOpenAiFeedback,
  createSubmissionOpenEndedAnswer,
  gradingSubmissionOpenEndedAnswer,
  getQuizDataForAboutPage,
  showAiHint,
  getSubmissionQuestionAIExplanation,
  getAiFeedback,
  addSubmissionSubQuestionAnswer,
} from './SubmissionApi';
import {
  adminGetSubmissionList,
  adminLoadMoreSubmissionList,
  adminGetSubmissionById,
  adminGetSubmissionQuestion,
} from './AdminSubmissionApi';
import { getUrlConvertedString } from '../../helper';
import { ISubTopic } from '../SubTopicSlice';

export type TSubmissionStatus = 'pending' | 'completed';
export type TSubmissionQuestionStatus = 'pending' | 'completed' | 'skipped';
export type TSubmissionMarkingStatus = 'pending' | 'completed';

export interface ISubmissionSubQuestion {
  _id: string;
  status: TSubmissionQuestionStatus;
  answer?: string;
  imageUrl?: string;
  verdict?: 'Correct' | 'Partially Correct' | 'Incorrect' | string;
  givenMarks?: number;
  analysis?: string;
}

export interface ISubmissionQuestion {
  order: number;
  question: string;
  status: TSubmissionQuestionStatus;
  answer?: IQuestionDetailAnswer;
  timerStart?: number;
  timerEnd?: number;
  openAiFeedback?: -1 | 0 | 1;
  openEnded?: {
    answer?: string;
    imageUrl?: string;
    score?: number;
    comment?: string;
    gradedAt?: number;
    gradedBy?: string;
    openAiComment?: string;
  };
  isOpenEnded?: boolean;
  isStructured?: boolean;
  subQuestions?: Array<ISubmissionSubQuestion>;
}

export interface ISubmission {
  _id: string;
  user: IUser;
  quiz: IQuiz;
  questions: ISubmissionQuestion[];
  score: number;
  shuffleFactor: number;
  status: TSubmissionStatus;
  totalScore: number;
  createdAt: string;
  completedAt: number;
  updatedAt?: string;
  isTimerEnabled?: boolean;
  timerStart?: number;
  timerEnd?: number;
  subTopic?: ISubTopic;
  aiFeedback?: {
    strength: string;
    improvement: string;
    recommendation: string;
  };
  markingStatus?: TSubmissionMarkingStatus;
}

export interface ISubmissionState {
  status: 'idle' | 'loading' | 'rejected';
  isListLoading: boolean;
  count: number;
  submission: ISubmission | undefined;
  question: IQuestion | undefined;
  list: Array<ISubmission>;
  quizDataForAboutPage?: { list: Array<{ paper: string; quiz: IQuiz }> };
  submitStatus: 'idle' | 'loading' | 'rejected';
  questionAIExplanation: string | undefined;
  aiExplanationStatus: 'idle' | 'loading' | 'rejected';
  aiFeedbackStatus: 'idle' | 'loading' | 'rejected';
}

const initialState: ISubmissionState = {
  status: 'idle',
  isListLoading: false,
  count: 0,
  submission: undefined,
  question: undefined,
  list: [],
  quizDataForAboutPage: undefined,
  submitStatus: 'idle',
  questionAIExplanation: undefined,
  aiExplanationStatus: 'idle',
  aiFeedbackStatus: 'idle',
};

export const SubmissionSlice = createSlice({
  name: 'submission',
  initialState,
  reducers: {
    resetSubmission: (state) => {
      state.submission = undefined;
    },
    resetSubmissionListData: (state) => {
      state.list = [];
      state.count = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET SUBMISSION LIST FOR USER & QUIZHISTORY
      .addCase(getSubmissionsForQuizHistory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubmissionsForQuizHistory.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(getSubmissionsForQuizHistory.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET SUBMISSION LIST FOR ADMINS
      .addCase(adminGetSubmissionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetSubmissionList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.count = action.payload.totalDocs;
        state.list = action.payload.docs;
      })
      .addCase(adminGetSubmissionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // LOAD MORE SUBMISSION LIST FOR USER & QUIZHISTORY
      .addCase(loadMoreSubmissionsForQuizHistory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadMoreSubmissionsForQuizHistory.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(loadMoreSubmissionsForQuizHistory.rejected, (state) => {
        state.status = 'rejected';
      })
      // LOAD MORE SUBMISSION LIST FOR ADMIN
      .addCase(adminLoadMoreSubmissionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminLoadMoreSubmissionList.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = [...state.list, ...action.payload.docs];
        state.list = newList;
        state.count = action.payload.totalDocs;
      })
      .addCase(adminLoadMoreSubmissionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // User Create Submission by Quiz Id
      .addCase(createNewSubmissionByQuizId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createNewSubmissionByQuizId.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.payload && action.payload.submission) {
          const { submission, subTopic } = action.payload;

          const newList = [...state.list, submission];

          state.list = newList;
          state.count += 1;

          const quizData = action.payload.submission.quiz;
          const topicData =
            quizData && quizData.topic ? quizData.topic : undefined;

          if (quizData && subTopic && topicData) {
            const urlConvertedCurriculum = getUrlConvertedString(
              topicData.curriculum
            );

            const urlConvertedSubject = getUrlConvertedString(
              topicData.subject
            );

            const urlConvertedTopic = getUrlConvertedString(topicData.name);

            const urlConvertedSubTopic = getUrlConvertedString(subTopic.name);

            const history = createBrowserHistory();

            history.push(
              `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopic._id}/quiz/${submission._id}`
            );

            window.location.reload();
          }
        }
      })
      .addCase(createNewSubmissionByQuizId.rejected, (state) => {
        state.status = 'rejected';
      })

      // USER CREATE SUBMISSION BY SUB TOPIC
      .addCase(createNewSubmissionBySubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createNewSubmissionBySubTopic.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.payload && action.payload.submission) {
          const { submission, subTopic } = action.payload;

          const newList = [...state.list, submission];

          state.list = newList;
          state.count += 1;

          const quizData = action.payload.submission.quiz;
          const topicData =
            quizData && quizData.topic ? quizData.topic : undefined;

          if (quizData && subTopic && topicData) {
            const urlConvertedCurriculum = getUrlConvertedString(
              topicData.curriculum
            );

            const urlConvertedSubject = getUrlConvertedString(
              topicData.subject
            );

            const urlConvertedTopic = getUrlConvertedString(topicData.name);

            const urlConvertedSubTopic = getUrlConvertedString(subTopic.name);

            const history = createBrowserHistory();

            history.push(
              `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopic._id}/quiz/${submission._id}`
            );

            window.location.reload();
          }
        }
      })
      .addCase(createNewSubmissionBySubTopic.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER GET SUBMISSION BY ID
      .addCase(getSubmissionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubmissionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(getSubmissionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET SUBMISSION BY ID ADMIN
      .addCase(adminGetSubmissionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetSubmissionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(adminGetSubmissionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER SUBMISSION ANSWER CREATE
      .addCase(createSubmissionAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSubmissionAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(createSubmissionAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER FINSIH SUBMISSION
      .addCase(finishSubmission.pending, (state) => {
        state.submitStatus = 'loading';
      })
      .addCase(finishSubmission.fulfilled, (state, action) => {
        state.submitStatus = 'idle';
        state.submission = action.payload;
      })
      .addCase(finishSubmission.rejected, (state) => {
        state.submitStatus = 'rejected';
      })
      // USER GET SUBMISSION QUESTION
      .addCase(getSubmissionQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSubmissionQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(getSubmissionQuestion.rejected, (state) => {
        state.status = 'rejected';
        state.question = undefined;
      })
      // GET SUBMISSION QUESTION BY ID ADMIN
      .addCase(adminGetSubmissionQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminGetSubmissionQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(adminGetSubmissionQuestion.rejected, (state) => {
        state.status = 'rejected';
        state.question = undefined;
      })
      // // GET AI HINT
      .addCase(showAiHint.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(showAiHint.fulfilled, (state, action) => {
        state.status = 'idle';
        if (
          state.question &&
          state.question._id === action.payload.questionId
        ) {
          state.question.aiHint = action.payload.aiHint;
        }
      })
      .addCase(showAiHint.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER GET SUBMISSION QUESTION ANSWER
      .addCase(showSubmissionQuestionAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(showSubmissionQuestionAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
        if (
          state.question &&
          action.payload &&
          state.submission &&
          state.submission.questions &&
          state.submission.questions.length > 0
        ) {
          const _currentQuestion = state.question;
          const _currentSubmission = state.submission;
          if (_currentQuestion._id === action.payload._id) {
            const index = _currentSubmission.questions.findIndex(
              (question) => question.question === _currentQuestion._id
            );
            if (index > -1) {
              _currentSubmission.questions[index].status = 'skipped';
              state.submission = _currentSubmission;
            }
          }
        }
      })
      .addCase(showSubmissionQuestionAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // UPDATE SUBMISSION COUNTDOWN SETTINGS
      .addCase(updateSubmissionCountdownSettings.fulfilled, (state, action) => {
        if (action.payload && state.submission) {
          state.submission = {
            ...state.submission,
            isTimerEnabled: action.payload.isTimerEnabled,
            timerStart: action.payload.timerStart,
          };
        }
      })
      // START SUBMISSION TIMER
      .addCase(startSubmissionQuestionTimer.fulfilled, (state, action) => {
        if (
          state.submission &&
          action.payload &&
          action.payload.submissionId &&
          action.payload.questionId &&
          action.payload.timerStart
        ) {
          const newSubmission = { ...state.submission };

          if (newSubmission.questions && newSubmission.questions.length > 0) {
            const newSubmissionQuestions = newSubmission.questions;
            const index = newSubmission.questions.findIndex(
              (item) => item.question === action.payload.questionId
            );

            if (index > -1) {
              newSubmissionQuestions[index].timerStart =
                action.payload.timerStart;
              newSubmission.questions = newSubmissionQuestions;
            }
          }

          state.submission = newSubmission;
        }
      })
      // END QUESTION TIMER
      .addCase(endSubmissionQuestionTimer.fulfilled, (state, action) => {
        if (
          state.submission &&
          action.payload &&
          action.payload.submissionId &&
          action.payload.questionId &&
          action.payload.timerEnd
        ) {
          const newSubmission = { ...state.submission };

          if (newSubmission.questions && newSubmission.questions.length > 0) {
            const newSubmissionQuestions = newSubmission.questions;
            const index = newSubmission.questions.findIndex(
              (item) => item.question === action.payload.questionId
            );

            if (index > -1) {
              newSubmissionQuestions[index].timerEnd = action.payload.timerEnd;
              newSubmission.questions = newSubmissionQuestions;
            }
          }

          state.submission = newSubmission;
        }
      })
      // updateSubmissionQuestionOpenAiFeedback
      .addCase(updateSubmissionQuestionOpenAiFeedback.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        updateSubmissionQuestionOpenAiFeedback.fulfilled,
        (state, action) => {
          state.status = 'rejected';

          if (
            state.submission &&
            state.submission._id === action.payload.submissionId
          ) {
            const newSubmission: ISubmission = {
              ...state.submission,
              questions: state.submission.questions.map((newQuestion) => {
                if (newQuestion.question === action.payload.questionId) {
                  newQuestion.openAiFeedback = action.payload.feedback;
                }

                return newQuestion;
              }),
            };

            state.submission = newSubmission;
          }
        }
      )
      .addCase(updateSubmissionQuestionOpenAiFeedback.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER SUBMISSION OPEN ENDED ANSWER CREATE
      .addCase(createSubmissionOpenEndedAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSubmissionOpenEndedAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(createSubmissionOpenEndedAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // TUTOR GRADING OPEN ENDED ANSWER
      .addCase(gradingSubmissionOpenEndedAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(gradingSubmissionOpenEndedAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(gradingSubmissionOpenEndedAnswer.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get Quiz Data For About Page
      .addCase(getQuizDataForAboutPage.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getQuizDataForAboutPage.fulfilled, (state, action) => {
        state.status = 'idle';
        state.quizDataForAboutPage = action.payload;
      })
      .addCase(getQuizDataForAboutPage.rejected, (state) => {
        state.status = 'rejected';
      })
      // USER GET SUBMISSION QUESTION
      .addCase(getSubmissionQuestionAIExplanation.pending, (state) => {
        state.aiExplanationStatus = 'loading';
        state.questionAIExplanation = undefined;
      })
      .addCase(
        getSubmissionQuestionAIExplanation.fulfilled,
        (state, action) => {
          state.aiExplanationStatus = 'idle';
          state.questionAIExplanation = action.payload;
        }
      )
      .addCase(getSubmissionQuestionAIExplanation.rejected, (state) => {
        state.aiExplanationStatus = 'rejected';
      })
      // USER GET SUBMISSION AI FEEDBACK
      .addCase(getAiFeedback.pending, (state) => {
        state.aiFeedbackStatus = 'loading';
        state.questionAIExplanation = undefined;
      })
      .addCase(getAiFeedback.fulfilled, (state, action) => {
        state.aiFeedbackStatus = 'idle';
        if (state.submission && action.payload.aiFeedback) {
          state.submission.aiFeedback = action.payload.aiFeedback;
        }
      })
      .addCase(getAiFeedback.rejected, (state) => {
        state.aiFeedbackStatus = 'rejected';
      })
      // USER ADD SUBMISSION SUB QUESTION ANSWER
      .addCase(addSubmissionSubQuestionAnswer.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addSubmissionSubQuestionAnswer.fulfilled, (state, action) => {
        state.status = 'idle';
        state.submission = action.payload;
      })
      .addCase(addSubmissionSubQuestionAnswer.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const {
  resetSubmission,
  resetSubmissionListData,
} = SubmissionSlice.actions;

export const getSubmissionState = (state: RootState): RootState['submission'] =>
  state.submission;

export * from './SubmissionApi';
export * from './AdminSubmissionApi';
export default SubmissionSlice.reducer;
